/**
 * animation-details.js
 *
 * Gère la vue détaillée d'animation en pleine page.
 *
 * Comportement :
 *   - gobooking:open-detail  → cache #go-agenda, affiche #gobooking-detail-view
 *   - Bouton "Retour" / fil d'ariane "Animations" → affiche #go-agenda, cache détail
 *   - history.pushState pour mettre à jour l'URL (animation=ID)
 *   - popstate pour gérer le bouton "précédent" du navigateur
 *
 * Fonctions exportées (compatibilité avec d'autres modules) :
 *   fillTemplate(data), initAnimationSessionsMap(id, mapId), fetchAnimationPartenaires(id)
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function esc(str) {
    if (!str) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function formatTime(t) {
    if (!t) return '';
    return String(t).slice(0, 5);
}

// ---------------------------------------------------------------------------
// AJAX helpers
// ---------------------------------------------------------------------------
async function fetchAnimationData(animationIdOrSlug) {
    try {
        const params = {
            action: 'gobooking_get_animation_details',
            nonce: goBookingData.nonce,
            include_featured_image: true,
        };
        if (typeof animationIdOrSlug === 'number' || /^\d+$/.test(String(animationIdOrSlug))) {
            params.animation_id = animationIdOrSlug;
        } else {
            params.animation_slug = animationIdOrSlug;
        }
        const response = await fetch(goBookingData.ajax_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams(params),
        });
        const result = await response.json();
        return result.success && result.data ? result.data : null;
    } catch {
        return null;
    }
}

async function fetchAnimationPartenaires(animationId) {
    try {
        const response = await fetch(goBookingData.ajax_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                action: 'gobooking_get_partenaires',
                nonce: goBookingData.nonce,
                animation_id: animationId,
            }),
        });
        const result = await response.json();
        return result.success && result.data?.partenaires ? result.data.partenaires : [];
    } catch {
        return [];
    }
}

// ---------------------------------------------------------------------------
// Rendu des sections de la vue détaillée
// ---------------------------------------------------------------------------
function renderCategories(categories) {
    if (!categories || categories.length === 0) return '';
    const chips = categories.map(cat => {
        const color = esc(cat.color || '#4ECDC4');
        return `<span class="gobooking-detail__cat-chip" style="--cat-color:${color};">${esc(cat.name)}</span>`;
    }).join('');
    return `<div class="gobooking-detail__cats">${chips}</div>`;
}

function renderAddress(data) {
    const sessions = Array.isArray(data.sessions) ? data.sessions : [];

    // Collecter les combinaisons uniques (lieu_depart + adresse + ville) par session
    const lines = [];
    const seen = new Set();
    sessions.forEach(s => {
        const parts = [s.lieu_depart, s.adresse, s.ville]
            .map(v => (v || '').trim())
            .filter(Boolean);
        if (parts.length === 0) return;
        const key = parts.join('|');
        if (!seen.has(key)) { seen.add(key); lines.push(parts.map(esc).join(', ')); }
    });

    // Fallback sur les metas de l'animation si aucune session n'a de données
    if (lines.length === 0) {
        const parts = [data.lieu_depart, data.adresse, data.ville]
            .map(v => (v || '').trim())
            .filter(Boolean);
        if (parts.length > 0) lines.push(parts.map(esc).join(', '));
    }

    if (lines.length === 0) return '';

    return `
<div class="gobooking-detail__info-block">
    <h3 class="gobooking-detail__section-title">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
            <circle cx="12" cy="9" r="2.5"/>
        </svg>
        Lieu de départ
    </h3>
    <p class="gobooking-detail__address">${lines.join('<br>')}</p>
</div>`;
}

function renderPlaces(data) {
    if (data.inscription_libre) {
        return `<span class="gobooking-detail__badge gobooking-detail__badge--libre">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <rect x="3" y="11" width="18" height="11" rx="2"/>
                <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
            </svg>
            Accès libre — sans inscription
        </span>`;
    }
    const n = parseInt(data.places_disponibles, 10);
    if (n === 0) return `<span class="gobooking-detail__badge gobooking-detail__badge--complet">Complet</span>`;
    if (n === -1) return `<span class="gobooking-detail__badge gobooking-detail__badge--ok">Places illimitées</span>`;
    return `<span class="gobooking-detail__badge gobooking-detail__badge--ok">${n} place${n > 1 ? 's' : ''} disponible${n > 1 ? 's' : ''}</span>`;
}

function renderInitiativeBadge(data) {
    if (!data.initiative_commune) return '';
    return `<span class="gobooking-detail__badge gobooking-detail__badge--initiative">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
        Initiatives locales
    </span>`;
}

function renderSessions(sessions) {
    if (!sessions || sessions.length === 0) return '';

    // Masquer la colonne "Places" si toutes les sessions ont une inscription externe
    const hidePlacesCol = sessions.every(
        s => s.disable_native_inscription && s.external_inscription_url
    );

    const rows = sessions.map(s => {
        const dateFin    = s.date_fin || s.date;
        const isMultiDay = dateFin && dateFin !== s.date && dateFin > s.date;
        const date       = isMultiDay
            ? `Du ${s.formatted_date || s.date} au ${s.formatted_date_fin || s.date_fin}`
            : (s.formatted_date || s.date || '');
        const day        = isMultiDay ? '' : (s.day_name || '');
        const h         = `${formatTime(s.heure_debut)}–${formatTime(s.heure_fin)}`;
        const lieu      = s.lieu_depart || '';
        const ville     = s.ville || '';
        const activite  = s.activite_titre || '';

        let placeBadge = '';
        // L'inscription externe est prioritaire sur inscription_libre
        if (s.disable_native_inscription && s.external_inscription_url) {
            const label = esc(s.external_inscription_label || 'S\'inscrire');
            placeBadge = `<a class="gobooking-detail__session-badge gobooking-detail__session-badge--external"
                href="${esc(s.external_inscription_url)}" target="_blank" rel="noopener noreferrer">${label}</a>`;
        } else if (s.inscription_libre) {
            placeBadge = `<span class="gobooking-detail__session-badge gobooking-detail__session-badge--libre">Accès libre</span>`;
        } else {
            const n = parseInt(s.places_disponibles, 10);
            if (n === 0) {
                placeBadge = `<span class="gobooking-detail__session-badge gobooking-detail__session-badge--complet">Complet</span>`;
            } else if (n === -1) {
                placeBadge = `<span class="gobooking-detail__session-badge gobooking-detail__session-badge--ok">Illimité</span>`;
            } else {
                placeBadge = `<span class="gobooking-detail__session-badge gobooking-detail__session-badge--ok">${n} pl.</span>`;
            }
        }

        const initiativeBadge = s.initiative_commune
            ? `<span class="gobooking-detail__session-badge gobooking-detail__session-badge--initiative"
                    title="Initiatives locales">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
               </span>` : '';

        return `
<tr class="gobooking-detail__session-row">
    <td class="gobooking-detail__session-cell gobooking-detail__session-cell--date">
        <span class="gobooking-detail__session-day">${esc(day)}</span>
        <span class="gobooking-detail__session-date">${esc(date)}</span>
    </td>
    <td class="gobooking-detail__session-cell gobooking-detail__session-cell--time">${esc(h)}</td>
    <td class="gobooking-detail__session-cell gobooking-detail__session-cell--lieu">
        ${esc(lieu)}${lieu && ville ? `<br><span class="gobooking-detail__session-ville">${esc(ville)}</span>` : (!lieu && ville ? esc(ville) : '')}
    </td>
    <td class="gobooking-detail__session-cell gobooking-detail__session-cell--activite">${esc(activite)}</td>
    ${!hidePlacesCol ? `<td class="gobooking-detail__session-cell gobooking-detail__session-cell--places">${placeBadge}${initiativeBadge}</td>` : ''}
</tr>`;
    }).join('');

    return `
<div class="gobooking-detail__info-block">
    <h3 class="gobooking-detail__section-title">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        Sessions
    </h3>
    <div class="gobooking-detail__sessions-wrapper">
        <table class="gobooking-detail__sessions-table">
            <thead>
                <tr>
                    <th>Date</th>
                    <th>Horaire</th>
                    <th>Lieu de départ</th>
                    <th>Activité</th>
                    ${!hidePlacesCol ? '<th>Places</th>' : ''}
                </tr>
            </thead>
            <tbody>${rows}</tbody>
        </table>
    </div>
</div>`;
}

function renderPartenaires(partenaires) {
    if (!partenaires || partenaires.length === 0) return '';

    const cards = partenaires.map(p => {
        let urlsHtml = '';
        if (p.urls && p.urls.length > 0) {
            const seen  = new Set();
            const links = p.urls.filter(u => {
                const key = `${u.url}-${u.label}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return !!u.url;
            }).map(u => `
<li class="gobooking-detail__partner-link">
    <span>${esc(u.label || 'Lien')}</span>
    <a href="${esc(u.url)}" target="_blank" rel="noopener noreferrer" class="gobooking-detail__partner-btn">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
            <polyline points="15 3 21 3 21 9"/>
            <line x1="10" y1="14" x2="21" y2="3"/>
        </svg>
        Visiter
    </a>
</li>`).join('');
            urlsHtml = `<ul class="gobooking-detail__partner-links">${links}</ul>`;
        }
        return `
<div class="gobooking-detail__partner-card">
    <div class="gobooking-detail__partner-name">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <rect x="2" y="7" width="20" height="14" rx="2"/>
            <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
        </svg>
        ${esc(p.nom || 'Partenaire')}
    </div>
    ${urlsHtml}
</div>`;
    }).join('');

    return `
<div class="gobooking-detail__info-block">
    <h3 class="gobooking-detail__section-title">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
        Partenaires
    </h3>
    <div class="gobooking-detail__partners-grid">${cards}</div>
</div>`;
}

function renderGallery(gallery) {
    if (!gallery || gallery.length === 0) return '';

    const slides = gallery.map((img, i) => `
<div class="gobooking-detail__slide${i === 0 ? ' gobooking-detail__slide--active' : ''}" data-index="${i}">
    <img src="${esc(img.url)}" alt="${esc(img.alt || '')}" loading="lazy">
    ${img.description ? `<div class="gobooking-detail__slide-caption">© ${esc(img.description)}</div>` : ''}
</div>`).join('');

    const dots = gallery.length > 1
        ? `<div class="gobooking-detail__gallery-dots">${gallery.map((_, i) =>
            `<button type="button" class="gobooking-detail__gallery-dot${i === 0 ? ' gobooking-detail__gallery-dot--active' : ''}" data-index="${i}" aria-label="Image ${i + 1}"></button>`
        ).join('')}</div>`
        : '';

    const arrows = gallery.length > 1
        ? `<button type="button" class="gobooking-detail__gallery-arrow gobooking-detail__gallery-arrow--prev" aria-label="Image précédente">
               <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
           </button>
           <button type="button" class="gobooking-detail__gallery-arrow gobooking-detail__gallery-arrow--next" aria-label="Image suivante">
               <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 6 15 12 9 18"/></svg>
           </button>`
        : '';

    const counter = gallery.length > 1
        ? `<span class="gobooking-detail__gallery-counter"><span class="gobooking-detail__gallery-current">1</span> / ${gallery.length}</span>`
        : '';

    return `
<div class="gobooking-detail__info-block">
    <h3 class="gobooking-detail__section-title">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
        </svg>
        Galerie
        ${counter}
    </h3>
    <div class="gobooking-detail__gallery" data-count="${gallery.length}">
        <div class="gobooking-detail__gallery-track">${slides}</div>
        ${arrows}
    </div>
    ${dots}
</div>`;
}

function initGallerySlider() {
    const gallery = document.querySelector('.gobooking-detail__gallery');
    if (!gallery) return;
    const count = parseInt(gallery.dataset.count, 10);
    if (count <= 1) return;

    let current = 0;
    const slides  = gallery.querySelectorAll('.gobooking-detail__slide');
    const dots    = gallery.parentElement.querySelectorAll('.gobooking-detail__gallery-dot');
    const counter = gallery.parentElement.querySelector('.gobooking-detail__gallery-current');

    function goTo(idx) {
        slides[current].classList.remove('gobooking-detail__slide--active');
        if (dots[current]) dots[current].classList.remove('gobooking-detail__gallery-dot--active');
        current = (idx + count) % count;
        slides[current].classList.add('gobooking-detail__slide--active');
        if (dots[current]) dots[current].classList.add('gobooking-detail__gallery-dot--active');
        if (counter) counter.textContent = current + 1;
    }

    gallery.querySelector('.gobooking-detail__gallery-arrow--prev')?.addEventListener('click', () => goTo(current - 1));
    gallery.querySelector('.gobooking-detail__gallery-arrow--next')?.addEventListener('click', () => goTo(current + 1));
    dots.forEach(d => d.addEventListener('click', () => goTo(parseInt(d.dataset.index, 10))));
}

function renderAccessibilite(data) {
    if (!data.accessibilite) return '';
    const info = data.informations_accessibilite || '';

    return `
<div class="gobooking-detail__info-block">
    <h3 class="gobooking-detail__section-title">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <circle cx="12" cy="12" r="10"/>
            <circle cx="12" cy="7" r="1.5" fill="currentColor" stroke="none"/>
            <path d="M12 10v5"/>
            <path d="M9 20l3-5 3 5"/>
            <path d="M8 13h8"/>
        </svg>
        Accessibilité
    </h3>
    <div class="gobooking-detail__accessibility">
        <span class="gobooking-detail__badge gobooking-detail__badge--accessible">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            Accessible
        </span>
        ${info ? `<p class="gobooking-detail__accessibility-info">${esc(info)}</p>` : ''}
    </div>
</div>`;
}

function renderInscriptionBtn(data) {
    // L'inscription externe est prioritaire sur inscription_libre
    if (data.disable_native_inscription && data.external_inscription_url) {
        const label = esc(data.external_inscription_label || "S'inscrire");
        return `
<div class="gobooking-detail__footer-actions">
    <a class="gobooking-detail__cta gobooking-detail__cta--external"
       href="${esc(data.external_inscription_url)}"
       target="_blank" rel="noopener noreferrer">${label}</a>
</div>`;
    }

    if (data.inscription_libre) return '';

    const places = parseInt(data.places_disponibles, 10);
    if (places === 0) {
        return `<div class="gobooking-detail__footer-actions">
            <span class="gobooking-detail__cta gobooking-detail__cta--disabled">Complet</span>
        </div>`;
    }

    return `
<div class="gobooking-detail__footer-actions">
    <button class="gobooking-detail__cta gobooking-detail__cta--primary"
            type="button"
            data-animation-id="${esc(String(data.id))}"
            id="gobooking-detail-inscrit-btn">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
        S'inscrire à l'animation
    </button>
</div>`;
}

function renderInscriptionBtnTop(data) {
    // L'inscription externe est prioritaire sur inscription_libre

    if (data.disable_native_inscription && data.external_inscription_url) {
        const label = esc(data.external_inscription_label || "S'inscrire");
        return `
<div class="gobooking-detail__top-actions">
    <a class="gobooking-detail__cta gobooking-detail__cta--external"
       href="${esc(data.external_inscription_url)}"
       target="_blank" rel="noopener noreferrer">${label}</a>
</div>`;
    }

    if (data.inscription_libre) return '';

    const places = parseInt(data.places_disponibles, 10);
    if (places === 0) {
        return `<div class="gobooking-detail__top-actions">
            <span class="gobooking-detail__cta gobooking-detail__cta--disabled">Complet</span>
        </div>`;
    }

    return `
<div class="gobooking-detail__top-actions">
    <button class="gobooking-detail__cta gobooking-detail__cta--primary"
            type="button"
            data-animation-id="${esc(String(data.id))}">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
        S'inscrire à l'animation
    </button>
</div>`;
}

// ---------------------------------------------------------------------------
// Construction du HTML complet du détail
// ---------------------------------------------------------------------------
function renderDetailContent(data) {
    const hasMap = data.latitude && data.longitude &&
        !isNaN(parseFloat(data.latitude)) && !isNaN(parseFloat(data.longitude)) &&
        !(parseFloat(data.latitude) === 0 && parseFloat(data.longitude) === 0);

    const featuredImg = data.featured_image
        ? `<div class="gobooking-detail__featured-img">
               <img src="${esc(data.featured_image)}" alt="${esc(data.title)}" loading="lazy">
           </div>`
        : '';

    const catHtml   = renderCategories(data.categories);
    const initiativeBadge = renderInitiativeBadge(data);
    const badgeHtml = initiativeBadge
        ? `<div class="gobooking-detail__badges">${initiativeBadge}</div>`
        : '';
    const excerptHtml = data.excerpt
        ? `<div class="gobooking-detail__excerpt">${data.excerpt}</div>`
        : '';
    const descHtml = data.description
        ? `<div class="gobooking-detail__info-block">
               <h3 class="gobooking-detail__section-title">
                   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                       <circle cx="12" cy="12" r="10"/>
                       <line x1="12" y1="8" x2="12" y2="12"/>
                       <line x1="12" y1="16" x2="12.01" y2="16"/>
                   </svg>
                   Description
               </h3>
               <div class="gobooking-detail__description">${data.description}</div>
           </div>`
        : '';
    const addrHtml      = renderAddress(data);
    const mapHtml       = hasMap
        ? `<div class="gobooking-detail__info-block">
               <div class="gobooking-detail__map-container" id="gobooking-detail-map"></div>
           </div>`
        : '';
    const ctaTopHtml    = renderInscriptionBtnTop(data);
    const sessionsHtml  = renderSessions(data.sessions);
    const galleryHtml   = renderGallery(data.gallery);
    const accessHtml    = renderAccessibilite(data);
    const partenHtml    = renderPartenaires(data.partenaires);
    const ctaHtml       = renderInscriptionBtn(data);

    return `
${featuredImg}
<div class="gobooking-detail__inner">
    ${catHtml}
    <h2 class="gobooking-detail__title">${esc(data.title)}</h2>
    ${badgeHtml}
    ${ctaTopHtml}
    ${excerptHtml}
    ${descHtml}
    ${addrHtml}
    ${mapHtml}
    ${sessionsHtml}
    ${galleryHtml}
    ${accessHtml}
    ${partenHtml}
    ${ctaHtml}
</div>`;
}

// ---------------------------------------------------------------------------
// Carte Leaflet dans la vue détaillée
// ---------------------------------------------------------------------------
async function initDetailMap(data) {
    const mapEl = document.getElementById('gobooking-detail-map');
    if (!mapEl || typeof window.L === 'undefined') return;
    const L = window.L;
    if (mapEl._leaflet_id) return;

    const lat = parseFloat(data.latitude);
    const lng = parseFloat(data.longitude);
    if (isNaN(lat) || isNaN(lng)) return;

    const map = L.map(mapEl, { scrollWheelZoom: false });
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        maxZoom: 19,
    }).addTo(map);

    const sessions = (data.sessions || []).filter(s => {
        const sLat = parseFloat(s.latitude);
        const sLng = parseFloat(s.longitude);
        return !isNaN(sLat) && !isNaN(sLng) && !(sLat === 0 && sLng === 0);
    });
    if (sessions.length > 0) {
        const bounds = [];
        sessions.forEach(s => {
            const sLat = parseFloat(s.latitude);
            const sLng = parseFloat(s.longitude);
            L.marker([sLat, sLng])
                .bindPopup(`<strong>${esc(s.formatted_date || s.date)}</strong><br>${esc(formatTime(s.heure_debut))}–${esc(formatTime(s.heure_fin))}<br>${esc(s.lieu_depart || '')}`)
                .addTo(map);
            bounds.push([sLat, sLng]);
        });
        if (bounds.length === 1) map.setView(bounds[0], 14);
        else if (bounds.length > 1) map.fitBounds(bounds, { padding: [24, 24] });
    } else {
        L.marker([lat, lng]).bindPopup(esc(data.lieu_depart || data.title)).addTo(map);
        map.setView([lat, lng], 14);
    }
    setTimeout(() => map.invalidateSize(), 300);
}

function waitForLeaflet(timeout = 5000) {
    return new Promise((resolve, reject) => {
        if (typeof L !== 'undefined') { resolve(); return; }
        const start = Date.now();
        const check = setInterval(() => {
            if (typeof L !== 'undefined') { clearInterval(check); resolve(); }
            else if (Date.now() - start > timeout) { clearInterval(check); reject(); }
        }, 100);
    });
}

// ---------------------------------------------------------------------------
// Gestion de la modale de détail
// ---------------------------------------------------------------------------
let _currentAnimId   = null;
let _currentAnimSlug = null;
let _currentAnimData = null;
let _lastFocusedEl   = null;

function getDetailEl()  { return document.getElementById('gobooking-detail-view'); }
function getDialogEl()  { return document.querySelector('.gobooking-detail-dialog'); }
function getContentEl() { return document.getElementById('gobooking-detail-content'); }
function getModalTitleEl() { return document.getElementById('gobooking-detail-modal-title'); }

function showDetailView(title) {
    const detail = getDetailEl();
    if (!detail) return;

    // Mémoriser l'élément focalisé pour le restaurer à la fermeture
    _lastFocusedEl = document.activeElement;

    // Mettre à jour le titre dans le header de la modale
    const titleEl = getModalTitleEl();
    if (titleEl) titleEl.textContent = title || '';

    detail.removeAttribute('hidden');
    document.body.classList.add('gobooking-modal-open');

    // Focaliser le dialog pour les lecteurs d'écran
    const dialog = getDialogEl();
    if (dialog) {
        dialog.focus();
        dialog.scrollTop = 0;
    }
}

function hideDetailView() {
    const detail = getDetailEl();
    if (!detail) return;

    detail.setAttribute('hidden', '');
    document.body.classList.remove('gobooking-modal-open');
    _currentAnimId   = null;
    _currentAnimSlug = null;

    // Restaurer le focus
    if (_lastFocusedEl && typeof _lastFocusedEl.focus === 'function') {
        _lastFocusedEl.focus();
    }
    _lastFocusedEl = null;
}

function showSkeleton() {
    const content = getContentEl();
    if (!content) return;
    content.innerHTML = `
<div class="gobooking-detail-skeleton" aria-live="polite">
    <div class="gobooking-detail-skeleton__img"></div>
    <div class="gobooking-detail-skeleton__line gobooking-detail-skeleton__line--title"></div>
    <div class="gobooking-detail-skeleton__line"></div>
    <div class="gobooking-detail-skeleton__line gobooking-detail-skeleton__line--short"></div>
</div>`;
}

function showError(msg) {
    const content = getContentEl();
    if (!content) return;
    content.innerHTML = `<div class="gobooking-detail__error">${esc(msg || 'Impossible de charger les détails de l\'animation.')}</div>`;
}

// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------
function initDetailView() {
    const detail = getDetailEl();
    if (!detail) return;

    // Bouton fermer ×
    document.getElementById('gobooking-detail-close-btn')?.addEventListener('click', closeModal);

    // Clic sur le backdrop (overlay) mais pas sur le dialog → fermer
    detail.addEventListener('click', e => {
        if (!e.target.closest('.gobooking-detail-dialog')) {
            closeModal();
        }
    });

    // Touche Escape → fermer
    document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && !detail.hasAttribute('hidden')) {
            closeModal();
        }
    });

    // Bouton S'inscrire dans la vue détail → ouvre modale inscription
    detail.addEventListener('click', e => {
        const btn = e.target.closest('.gobooking-detail__cta--primary');
        if (!btn) return;
        const animId = parseInt(btn.dataset.animationId, 10);
        if (!animId) return;
        if (typeof window.goBookingOpenInscription === 'function') {
            const d = _currentAnimData || {};
            window.goBookingOpenInscription(animId, d.title || '', {
                places_disponibles         : d.places_disponibles ?? 0,
                lieu_depart                : d.lieu_depart || '',
                disable_native_inscription : d.disable_native_inscription || false,
                external_inscription_url   : d.external_inscription_url || '',
                external_inscription_label : d.external_inscription_label || '',
                require_taille_cm          : d.require_taille_cm || false,
                max_inscriptions_user      : d.max_inscriptions_user || 2,
            });
        } else {
            document.dispatchEvent(new CustomEvent('gobooking:open-inscription', {
                detail: { animationId: animId },
                bubbles: true,
            }));
        }
    });

    // Écoute l'événement émis par agenda.js
    document.addEventListener('gobooking:open-detail', async (e) => {
        const animationId = e.detail?.animationId;
        const slug        = e.detail?.slug || '';
        if (!animationId && !slug) return;

        _currentAnimId   = animationId;
        _currentAnimSlug = slug;

        showSkeleton();
        showDetailView('Chargement…');

        // Mettre à jour l'URL avec le slug (lisible) ou l'ID en repli.
        // Le paramètre s'appelle « anim » (et non « animation ») pour éviter
        // un conflit avec le query_var du CPT animation enregistré par WordPress.
        const url = new URL(window.location.href);
        url.searchParams.set('anim', slug || animationId);
        history.pushState({ animationId, slug }, '', url.toString());

        // Charger les données (par ID si disponible, sinon par slug)
        const data = await fetchAnimationData(animationId || slug);
        if (!data) { showError(); return; }

        // Mettre à jour le slug si on ne l'avait pas encore
        if (!slug && data.slug) {
            _currentAnimSlug = data.slug;
            const url2 = new URL(window.location.href);
            url2.searchParams.set('anim', data.slug);
            history.replaceState({ animationId, slug: data.slug }, '', url2.toString());
        }

        // Stocker les données courantes pour les réutiliser (ex: modale inscription)
        _currentAnimData = data;

        // Mettre à jour le titre dans le header de la modale
        const titleEl = getModalTitleEl();
        if (titleEl) titleEl.textContent = data.title || 'Animation';

        const content = getContentEl();
        if (content) {
            const dialog = getDialogEl();
            if (dialog) dialog.scrollTop = 0;
            content.innerHTML = renderDetailContent(data);
        }

        // Slider galerie
        initGallerySlider();

        // Carte OSM
        const lat = parseFloat(data.latitude);
        const lng = parseFloat(data.longitude);
        if (!isNaN(lat) && !isNaN(lng) && !(lat === 0 && lng === 0)) {
            if (typeof window.L !== 'undefined') {
                initDetailMap(data);
            } else {
                waitForLeaflet().then(() => initDetailMap(data)).catch(() => {});
            }
        }
    });

    // Gestion du bouton "précédent" du navigateur → ferme la modale
    window.addEventListener('popstate', (e) => {
        if (e.state?.animationId || e.state?.slug) {
            document.dispatchEvent(new CustomEvent('gobooking:open-detail', {
                detail: { animationId: e.state.animationId, slug: e.state.slug || '' },
                bubbles: true,
            }));
        } else {
            if (!detail.hasAttribute('hidden')) {
                hideDetailView();
            }
        }
    });

    // Si la page est chargée avec ?anim=slug-ou-id, ouvrir directement la modale
    const urlParams    = new URLSearchParams(window.location.search);
    const initialParam = urlParams.get('anim');
    if (initialParam) {
        const isNumeric    = /^\d+$/.test(initialParam);
        const initialId    = isNumeric ? parseInt(initialParam, 10) : 0;
        const initialSlug  = isNumeric ? '' : initialParam;
        history.replaceState({ animationId: initialId, slug: initialSlug }, '', window.location.href);
        document.dispatchEvent(new CustomEvent('gobooking:open-detail', {
            detail: { animationId: initialId, slug: initialSlug },
            bubbles: true,
        }));
    }
}

function closeModal() {
    const url = new URL(window.location.href);
    url.searchParams.delete('anim');
    history.pushState({}, '', url.toString());
    hideDetailView();
}

// ---------------------------------------------------------------------------
// Point d'entrée
// ---------------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
    initDetailView();
});

// ---------------------------------------------------------------------------
// Legacy : carte multi-sessions (utilisée par d'autres modules)
// ---------------------------------------------------------------------------
async function fetchAnimationSessionsLegacy(animationId) {
    try {
        const response = await fetch(goBookingData.ajax_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                action: 'gobooking_get_sessions',
                nonce: goBookingData.nonce,
                animation_id: animationId,
            }),
        });
        const result = await response.json();
        return result.success && result.data?.sessions ? result.data.sessions : [];
    } catch {
        return [];
    }
}

async function initAnimationSessionsMap(animationId, mapContainerId) {
    try {
        const mapContainer = document.getElementById(mapContainerId);
        if (!mapContainer || typeof window.L === 'undefined') return null;
        const L = window.L;
        if (mapContainer._leaflet_id) return null;

        const sessions = await fetchAnimationSessionsLegacy(animationId);
        const validLocations = sessions
            .filter(s => s.latitude && s.longitude)
            .map((s, i) => ({
                lat: parseFloat(s.latitude),
                lng: parseFloat(s.longitude),
                title: s.animation_titre || 'Animation',
                date: s.formatted_date || s.date,
                horaire: s.horaire || `${s.heure_debut} - ${s.heure_fin}`,
                places: s.places_disponibles,
                lieu_depart: s.lieu_depart,
            }));

        if (validLocations.length === 0) {
            mapContainer.style.display = 'none';
            return null;
        }

        const map = L.map(mapContainerId);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            maxZoom: 19,
        }).addTo(map);

        const bounds = L.latLngBounds();
        validLocations.forEach(loc => {
            L.marker([loc.lat, loc.lng])
                .bindPopup(`<strong>${esc(loc.title)}</strong><br>${esc(loc.date)}<br>${esc(loc.lieu_depart || '')}`)
                .addTo(map);
            bounds.extend([loc.lat, loc.lng]);
        });

        if (validLocations.length === 1) map.setView([validLocations[0].lat, validLocations[0].lng], 14);
        else map.fitBounds(bounds, { padding: [30, 30] });

        setTimeout(() => map.invalidateSize(), 500);
        return map;
    } catch {
        return null;
    }
}

// ---------------------------------------------------------------------------
// Legacy template — utilisé par calendar.js
// ---------------------------------------------------------------------------
const TEMPLATE = `
<div class="fc-flexbox-event-details-content">
    <div class="fc-flexbox-details-content">
        <div class="event-full-details">
            <div class="event-featured-image" id="featured-image-\${id}" style="display: none;">
                <img src="" alt="\${title}" class="event-thumbnail img-fluid mb-3">
            </div>
            <div class="event-description" style="display: \${description ? 'block' : 'none'}">
                <h6>Description</h6>
                <p>\${description}</p>
                \${inscription_btn_html}
            </div>
            \${map_html}
            <div class="event-partenaires" style="display: \${partenaires && partenaires.length > 0 ? 'block' : 'none'}">
                <div class="partenaires-cards-container">
                    \${partenaires_html}
                </div>
            </div>
            \${inscription_btn_html}
        </div>
    </div>
</div>`;

function generatePartenairesHTML(partenaires) {
    if (!partenaires || partenaires.length === 0) return '';
    let html = '<h6>Partenaires de l\'activité</h6><div class="row row-cols-1 row-cols-md-2 g-4">';
    partenaires.forEach(p => {
        if (typeof p !== 'object' || p === null) return;
        let urls = Array.isArray(p.urls) ? p.urls
            : typeof p.urls === 'object' ? Object.values(p.urls)
            : p.url ? [{ url: p.url, label: p.label || 'Lien' }]
            : [];
        const seen = new Set();
        let urlsHtml = urls.length > 0 ? '<ul class="list-group list-group-flush">' : '<div class="card-footer text-muted"><small>Aucun lien disponible</small></div>';
        if (urls.length > 0) {
            urls.forEach(u => {
                const url   = typeof u === 'object' ? (u.url || '') : u;
                const label = typeof u === 'object' ? (u.label || 'Lien') : 'Lien';
                if (!url) return;
                const key = `${url}-${label}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    urlsHtml += `<li class="list-group-item d-flex justify-content-between align-items-center"><span>${label}</span><a href="${url}" class="btn btn-sm btn-outline-primary" target="_blank"><i class="bi bi-box-arrow-up-right me-1"></i>Visiter</a></li>`;
                }
            });
            urlsHtml += '</ul>';
        }
        html += `<div class="col"><div class="card h-100 shadow-sm"><div class="card-body"><h5 class="card-title"><i class="bi bi-building me-2"></i>${p.nom || 'Partenaire'}</h5></div>${urlsHtml}</div></div>`;
    });
    html += '</div>';
    return html;
}

function fillTemplate(data) {
    if (data.partenaires && data.partenaires.length > 0) {
        data.partenaires_html = generatePartenairesHTML(data.partenaires);
    } else {
        data.partenaires_html = '';
    }
    if (data.featured_image && typeof data.featured_image !== 'string') {
        data.featured_image = String(data.featured_image);
    }
    const places = parseInt(data.places_disponibles || '0', 10);
    data.show_inscription_btn = places > 0;
    data.inscription_btn_html = data.show_inscription_btn
        ? (data.disable_native_inscription && data.external_inscription_url
            ? `<div class="event-actions"><a href="${data.external_inscription_url}" class="go-booking-inscription-btn" target="_blank">${data.external_inscription_label || "S'inscrire"}</a></div>`
            : `<div class="event-actions"><button class="go-booking-inscription-btn" data-toggle="modal" data-event-id="${data.id}">S'inscrire à l'animation</button></div>`)
        : '';
    const lat = parseFloat(data.latitude), lng = parseFloat(data.longitude);
    const hasMap = data.latitude != null && !isNaN(lat) && !(lat === 0 && lng === 0);
    data.map_html = hasMap ? `<div class="fc-event-map-container"><div id="map-${data.id}" class="animation-map"></div></div>` : '';
    return TEMPLATE.replace(/\${(\w+)}/g, (match, key) => {
        if (key === 'categories' && data[key]) {
            return data[key].map(cat => typeof cat === 'object' && cat.name
                ? `<span class="event-category-tag" style="background-color:${cat.color};">${cat.name}</span>`
                : `<span class="event-category-tag">${cat}</span>`
            ).join('');
        }
        return data[key] !== undefined ? data[key] : match;
    });
}

function initFeaturedImage(container, data) {
    if (!container || !data) return;
    const imgContainer = container.querySelector(`#featured-image-${data.id}`);
    if (!imgContainer) return;
    const img = imgContainer.querySelector('img');
    if (!img) return;
    if (data.featured_image) { img.src = data.featured_image; imgContainer.style.display = 'block'; }
    else imgContainer.style.display = 'none';
}

// ---------------------------------------------------------------------------
// Expositions globales
// ---------------------------------------------------------------------------
window.initAnimationSessionsMap = initAnimationSessionsMap;
window.fillTemplate = fillTemplate;
window.fetchAnimationPartenaires = fetchAnimationPartenaires;

export { TEMPLATE, fillTemplate, initAnimationSessionsMap, fetchAnimationPartenaires };
